import { useEffect, useId, useState } from 'react';
import { Camera, FileText, X } from 'lucide-react';
import salesService from '../../services/sales';
import type { CrmFile } from '../../types/sales';

/**
 * Photos on a note, follow-up or update. The file route needs the bearer
 * token, so each is fetched as a blob and shown from an object URL; tapping
 * one opens it full size. A PDF shows as its name.
 */

function Thumb({ file, onRemove }: { file: CrmFile; onRemove?: (id: number) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const isImage = !file.mime_type || file.mime_type.startsWith('image/');

  useEffect(() => {
    if (!isImage) return undefined;
    let made: string | null = null;
    let live = true;
    salesService.fileBlob(file.id)
      .then((blob) => { if (live) { made = URL.createObjectURL(blob); setUrl(made); } })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [file.id, isImage]);

  const open = async () => {
    const target = url ?? URL.createObjectURL(await salesService.fileBlob(file.id));
    window.open(target, '_blank', 'noopener');
  };

  return (
    <li className="relative">
      <button type="button" onClick={open} aria-label={`Open ${file.file_name}`}
        className="block h-20 w-20 overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50">
        {isImage && url ? (
          <img src={url} alt={file.file_name} className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full flex-col items-center justify-center gap-1 p-1 text-xs text-neutral-600 break-all">
            <FileText className="h-5 w-5" aria-hidden />
            {failed ? 'Could not load' : isImage ? 'Loading' : file.file_name}
          </span>
        )}
      </button>
      {onRemove && (
        <button type="button" onClick={() => onRemove(file.id)} aria-label={`Remove ${file.file_name}`}
          className="absolute -right-2 -top-2 rounded-full border border-neutral-300 bg-white p-0.5 text-neutral-600 hover:bg-neutral-100">
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </li>
  );
}

export function CrmFileList({ files, onRemove }: { files: CrmFile[]; onRemove?: (id: number) => void }) {
  if (!files.length) return null;
  return (
    <ul className="flex flex-wrap gap-3" aria-label="Photos">
      {files.map((f) => <Thumb key={f.id} file={f} onRemove={onRemove} />)}
    </ul>
  );
}

/**
 * Photos chosen before saving: a phone offers its camera and its library.
 * They are sent once what they belong to has been saved (`uploadAll`).
 */
export function PhotoPicker({ files, onChange, label = 'Add photos' }: { files: File[]; onChange: (files: File[]) => void; label?: string }) {
  const id = useId();
  return (
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      <label htmlFor={id}
        className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
        <Camera className="h-4 w-4" aria-hidden />{label}
      </label>
      <input id={id} type="file" accept="image/*,application/pdf" multiple className="sr-only"
        onChange={(e) => { onChange([...files, ...Array.from(e.target.files ?? [])]); e.target.value = ''; }} />
      {files.map((f, i) => (
        <span key={`${f.name}-${i}`} className="inline-flex max-w-full items-center gap-1 rounded-md bg-neutral-100 px-2 py-1 text-xs text-neutral-700">
          <span className="truncate max-w-[10rem]">{f.name}</span>
          <button type="button" aria-label={`Take off ${f.name}`} onClick={() => onChange(files.filter((_, j) => j !== i))}>
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </span>
      ))}
    </div>
  );
}
